import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import {
  Bdkv2OAuthFlow,
  type Bdkv2BookAuthenticationDescriptor,
  type Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  V1OAuthGrantType,
  V1SecretState,
  type V1OAuthApp,
} from '@/generated/hooks/gringotts/models';
import { getListOAuthAppsQueryOptions } from '@/generated/hooks/gringotts/v1/services/oauth_app_service';
import { getListSecretsQueryOptions } from '@/generated/hooks/gringotts/v1/services/secret_service';
import { getGetSecretTypeQueryOptions } from '@/generated/hooks/gringotts/v1/services/secret_type_service';
import {
  GRINGOTTS_MAX_PAGE_SIZE,
  isCredentialUsableInWorkspace,
  secretTypeFilter,
  type TCredential,
} from '@/modules/credentials';

const SHARED_SCOPE = 'shared';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether an auth method is one BDK synthesizes from a ManagedAuthentication
 * resource. Those carry the resource's UID as their id; real methods carry the
 * book's own ids (`api_key`, `oauth_authorization_code`, …).
 *
 * @param auth - An auth method from the book descriptor.
 * @returns True for a ManagedAuthentication entry.
 */
export const isManagedAuthenticationEntry = (
  auth: Bdkv2BookAuthenticationDescriptor,
): boolean => UUID_PATTERN.test(auth.custom?.id || auth.oauth?.id || '');

/** A step-1 entry backed by a shared Gringotts OAuth app or secret. */
export type TSharedAuthEntry =
  | {
      kind: 'oauthApp';
      /** The entry's auth method id in the wizard. */
      id: string;
      /** The book's own auth method id, sent to BDK. */
      rawAuthMethodId: string;
      /** The book's own auth method, for signing in through another app. */
      rawAuthentication: Bdkv2BookAuthenticationDescriptor;
      secretTypeName: string;
      app: V1OAuthApp;
    }
  | {
      kind: 'secret';
      id: string;
      rawAuthMethodId: string;
      secretTypeName: string;
      secret: TCredential;
    };

type TQueryResult<TData> = { data?: TData; isLoading: boolean };

// Stable, so each list stays the same reference until a query changes.
const combineData = <TData>(results: TQueryResult<TData>[]) => ({
  data: results.map(result => result.data),
  isLoading: results.some(result => result.isLoading),
});

const authMethodId = (auth: Bdkv2BookAuthenticationDescriptor) =>
  auth.custom?.id || auth.oauth?.id || '';

/**
 * The OAuth app's arguments a user fills in when signing in: those without a
 * constant value or a capture from the token response.
 *
 * @param app - The OAuth app.
 * @returns The arguments to ask for.
 */
export const oauthArgumentsToFill = (app: V1OAuthApp) =>
  (app.config?.arguments ?? []).filter(
    argument => !argument.constant && !argument.capture,
  );

const isClientCredentialsApp = (app: V1OAuthApp) =>
  app.config?.grant_type ===
  V1OAuthGrantType.OAUTH_GRANT_TYPE_CLIENT_CREDENTIALS;

/**
 * The auth methods the connect wizard offers for an integration that stores
 * its credentials in Gringotts. Gringotts' shared OAuth apps and shared
 * secrets replace BDK's ManagedAuthentication: each becomes a preferred entry
 * shaped exactly like a ManagedAuthentication one (an OAuth app → a "Sign in
 * with …" entry; a secret → an entry with nothing to fill in). BDK's own
 * ManagedAuthentication entries are dropped. The book's own key/token methods
 * stay, and so do OAuth methods no shared app covers; those sign in through an
 * OAuth app the organization registers.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @param integration - The book descriptor.
 * @param enabled - Whether to replace the methods at all.
 * @param fetch - Whether to load the shared apps and secrets; when off, what
 *   was loaded before is used.
 * @returns The methods to offer, the shared entries by id, the signed-in OAuth
 *   credentials per secret type, the organization's and workspace's own OAuth
 *   apps per secret type, and the loading state.
 */
export function useSharedAuthentications(
  organizationId: string,
  workspaceId: string,
  integration: Bdkv2BookDescriptor,
  enabled: boolean,
  fetch = true,
) {
  const { rawMethods, oauthMethods, secretTypeNames } = useMemo(() => {
    const methods = (integration.authentications ?? []).filter(
      auth =>
        !isManagedAuthenticationEntry(auth) &&
        !auth.oauth?.deprecated &&
        !auth.custom?.deprecated &&
        !!auth.secret_type_id,
    );
    return {
      rawMethods: methods,
      oauthMethods: methods.filter(auth => auth.oauth),
      secretTypeNames: [
        ...new Set(methods.map(auth => auth.secret_type_id ?? '')),
      ],
    };
  }, [integration.authentications]);
  const shouldFetch = enabled && fetch;

  const apps = useQueries({
    queries: oauthMethods.map(auth =>
      getListOAuthAppsQueryOptions(
        organizationId,
        {
          filter: `secret_type = "${auth.secret_type_id}"`,
          page_size: GRINGOTTS_MAX_PAGE_SIZE,
        },
        { query: { enabled: shouldFetch && !!organizationId } },
      ),
    ),
    combine: combineData,
  });

  const types = useQueries({
    queries: secretTypeNames.map(name =>
      getGetSecretTypeQueryOptions(name, { query: { enabled: shouldFetch } }),
    ),
    combine: combineData,
  });
  const typeIds = types.data.map(type => type?.data.id ?? '');

  const secrets = useQueries({
    queries: typeIds.map(typeId =>
      getListSecretsQueryOptions(
        organizationId,
        {
          filter: secretTypeFilter(typeId),
          page_size: GRINGOTTS_MAX_PAGE_SIZE,
        },
        {
          query: { enabled: shouldFetch && !!organizationId && !!typeId },
        },
      ),
    ),
    combine: combineData,
  });

  const isLoading =
    enabled && (apps.isLoading || types.isLoading || secrets.isLoading);

  const derived = useMemo(
    () =>
      deriveAuthentications({
        organizationId,
        workspaceId,
        integration,
        enabled,
        rawMethods,
        oauthMethods,
        secretTypeNames,
        appData: apps.data.map(response => response?.data.oauth_apps),
        secretData: secrets.data.map(response => response?.data.secrets),
      }),
    [
      organizationId,
      workspaceId,
      integration,
      enabled,
      rawMethods,
      oauthMethods,
      secretTypeNames,
      apps.data,
      secrets.data,
    ],
  );

  return useMemo(() => ({ ...derived, isLoading }), [derived, isLoading]);
}

function deriveAuthentications({
  organizationId,
  workspaceId,
  integration,
  enabled,
  rawMethods,
  oauthMethods,
  secretTypeNames,
  appData,
  secretData,
}: {
  organizationId: string;
  workspaceId: string;
  integration: Bdkv2BookDescriptor;
  enabled: boolean;
  rawMethods: Bdkv2BookAuthenticationDescriptor[];
  oauthMethods: Bdkv2BookAuthenticationDescriptor[];
  secretTypeNames: string[];
  appData: (V1OAuthApp[] | undefined)[];
  secretData: (TCredential[] | undefined)[];
}) {
  const entries = new Map<string, TSharedAuthEntry>();
  const credentialsByType = new Map<string, TCredential[]>();
  const ownAppsByType = new Map<string, V1OAuthApp[]>();
  if (!enabled) {
    return {
      authentications: integration.authentications ?? [],
      entries,
      credentialsByType,
      ownAppsByType,
    };
  }

  const secretsByType = new Map<string, TCredential[]>();
  secretTypeNames.forEach((name, index) =>
    secretsByType.set(name, secretData[index] ?? []),
  );

  const orgScope = `organizations/${organizationId}`;
  oauthMethods.forEach((auth, index) => {
    const typeName = auth.secret_type_id ?? '';
    ownAppsByType.set(
      typeName,
      (appData[index] ?? []).filter(
        app =>
          app.scope === orgScope ||
          app.scope === `${orgScope}/workspaces/${workspaceId}`,
      ),
    );
    // Accounts already signed in, through whichever app.
    credentialsByType.set(
      typeName,
      (secretsByType.get(typeName) ?? []).filter(
        credential =>
          credential.state === V1SecretState.SECRET_STATE_ACTIVE &&
          isCredentialUsableInWorkspace(
            credential,
            organizationId,
            workspaceId,
          ),
      ),
    );
  });

  const typesWithSharedApp = new Set<string>();
  const oauthEntries: Bdkv2BookAuthenticationDescriptor[] = [];
  oauthMethods.forEach((auth, index) => {
    const typeName = auth.secret_type_id ?? '';
    (appData[index] ?? [])
      .filter(app => app.scope === SHARED_SCOPE)
      .forEach(app => {
        const id = `shared-oauth-app:${app.id}`;
        typesWithSharedApp.add(typeName);
        entries.set(id, {
          kind: 'oauthApp',
          id,
          rawAuthMethodId: authMethodId(auth),
          rawAuthentication: auth,
          secretTypeName: typeName,
          app,
        });
        oauthEntries.push({
          ...auth,
          oauth: {
            ...auth.oauth,
            id,
            name: app.display_name,
            flows: [
              isClientCredentialsApp(app)
                ? Bdkv2OAuthFlow.OAuthFlowClientCredentials
                : Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
            ],
            credentials: [],
            arguments: [],
            preferred: true,
          },
        });
      });
  });

  const secretEntries: Bdkv2BookAuthenticationDescriptor[] = [];
  rawMethods
    .filter(auth => auth.custom)
    .forEach(auth => {
      const typeName = auth.secret_type_id ?? '';
      (secretsByType.get(typeName) ?? [])
        .filter(secret => secret.scope === SHARED_SCOPE)
        .forEach(secret => {
          const id = `shared-secret:${secret.id}`;
          entries.set(id, {
            kind: 'secret',
            id,
            rawAuthMethodId: authMethodId(auth),
            secretTypeName: typeName,
            secret,
          });
          secretEntries.push({
            ...auth,
            custom: {
              ...auth.custom,
              id,
              name: secret.display_name,
              credentials: [],
              preferred: true,
            },
          });
        });
    });

  // A book OAuth method stays only where no shared app covers it (signing in
  // through the organization's own app is then an option of the shared entry).
  const isOfferedBookMethod = (auth: Bdkv2BookAuthenticationDescriptor) =>
    !!auth.custom || !typesWithSharedApp.has(auth.secret_type_id ?? '');

  return {
    authentications: [
      ...oauthEntries,
      ...secretEntries,
      ...rawMethods.filter(isOfferedBookMethod),
    ],
    entries,
    credentialsByType,
    ownAppsByType,
  };
}
